<script lang="ts" generics="T extends SbBlokData">
  import "swiper/scss";
  import "swiper/scss/navigation";
  import "swiper/scss/pagination";

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  import type { SbBlokData } from "@storyblok/svelte";

  // @ts-expect-error no types for base swiper module
  import { A11y, Pagination } from "swiper";

  import { Swiper, SwiperSlide } from "swiper/svelte";
  import gsap from "gsap";
  import Button from "$lib/atoms/Button.svelte";
  import { ButtonColor, IconType } from "$types/atoms";
  import { displayType } from "$lib/utils/breakpoints";
  import { lazyloadImg } from "$stores";

  export let items: T[] = [];
  export let slidesPerView = 1;
  export let slidesPerViewMd: number | null = null;
  export let spaceBetween = 0;
  export let pagination = true;
  export let initialSlide = 0;
  export let controls: "top" | "bottom" = "bottom";
  export let headline: string = "";

  let slider: Swiper;

  function swiperReady(e: CustomEvent) {
    slider = e.detail[0];
    if (slider) {
      gsap.to(slider.slides, { opacity: 1, duration: 0.5, stagger: 0.1 });
    }
  }

  $: {
    if ($displayType !== "mobile") {
      slider?.pagination.disable();
    } else {
      slider?.pagination.enable();
    }

    setTimeout(() => {
      if ($lazyloadImg) {
        $lazyloadImg.update();
      }
    }, 0);
  }

  export function slidePrev() {
    slider?.slidePrev();
  }

  export function slideNext() {
    slider?.slideNext();
  }
</script>

{#if controls === "top" && pagination}
  <div
    class="flex flex-row order-1 md:order-none mb-[36px] md:mb-[46px] gap-[80px] lg:gap-[120px]"
  >
    {#if headline}
      <h2 class="type:h2 text-pretty">{headline}</h2>
    {/if}
    <div class="controls flex flex-row justify-end mt-[20px]">
      <Button
        icon={IconType.CHEVRON_LEFT}
        color={ButtonColor.GRAY}
        reduced
        on:click={slidePrev}
      />
      <Button
        icon={IconType.CHEVRON_RIGHT}
        color={ButtonColor.GRAY}
        reduced
        on:click={slideNext}
      />
    </div>
  </div>
{/if}

<Swiper
  bind:this={slider}
  breakpoints={{
    600: {
      slidesPerView: slidesPerViewMd ? slidesPerViewMd : slidesPerView,
    },
  }}
  {...{ class: `swiper-custom disable-overflow ${$$props.class}` }}
  grabCursor={true}
  {initialSlide}
  modules={[Pagination, A11y]}
  navigation
  on:swiper={swiperReady}
  pagination={pagination ? { clickable: true } : false}
  resistance={true}
  resistanceRatio={0.5}
  {slidesPerView}
  {spaceBetween}
>
  {#each items as item, index}
    <SwiperSlide>
      <div class="slide">
        <slot name="slide" prop={item} {index} />
      </div>
    </SwiperSlide>
  {/each}
</Swiper>

<div
  class="bottom-bar"
  class:has-slot={$$slots.bottom}
  class:shown={controls !== "top"}
>
  {#if $$slots.bottom}
    <slot name="bottom" />
  {/if}
  {#if pagination && controls != "top"}
    <div class="controls">
      <Button
        icon={IconType.CHEVRON_LEFT}
        color={ButtonColor.GRAY}
        on:click={slidePrev}
      />
      <Button
        icon={IconType.CHEVRON_RIGHT}
        color={ButtonColor.GRAY}
        on:click={slideNext}
      />
    </div>
  {/if}
</div>

<style lang="scss">
  .bottom-bar {
    justify-content: center;
    display: none;

    &.shown {
      margin-bottom: 56px;
      @include breakpoint("small") {
        display: flex;
        margin-top: fluid(80px, 56px);
        margin-bottom: 0;
      }
    }

    &.has-slot.shown {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .controls {
    display: none;
    column-gap: 8px;

    @include breakpoint("small") {
      display: flex;
    }
  }
</style>
